<template>
   <LoadingPage v-if="isLoading"></LoadingPage>

   <div v-if="isEditing" class="row mb-2">
      <h6 class="card-subtitle text-muted mb-2">Created: <strong>{{ lead.dateCreated }}</strong></h6>
   </div>

   <form ref="leadFormRef" class="modal-form-custom row g-3">

      <div v-if="isAuthSynced" class="col-12">
         <div class="alert alert-warning py-2 mb-2 small text-center" role="alert" style="border-radius: 8px; font-size: 13px;">
            <i class="fa-solid fa-circle-info me-2"></i>
            This lead is synchronized from the Auth service and cannot be edited.
         </div>
      </div>

      <div class="col-12">
         <label for="organisation" class="form-label fw-bold">Organisation Name <span class="text-danger">*</span></label>
         <select v-if="abnRecordFound && organisationNameOptions.length" id="organisation" class="form-select" v-model="lead.organisation" @change="v$.lead.organisation.$touch" :disabled="isAuthSynced">
            <option v-for="name in organisationNameOptions" :key="name" :value="name">{{ name }}</option>
         </select>
         <input v-else type="text" id="organisation" v-model.trim="lead.organisation" @input="handleOrgInput" @blur="v$.lead.organisation.$touch"
            class="form-control" placeholder="Organisation Name" :disabled="isAuthSynced" />

         <small v-if="lead.original_organisation_name && lead.original_organisation_name !== lead.organisation" class="text-muted fst-italic d-block mt-1" style="font-size: 0.8rem;">
            <i class="fa-solid fa-history me-1"></i> Formerly: {{ lead.original_organisation_name }}
         </small>

         <span class="form-text text-danger" v-if="v$.lead.organisation.$error">
            <span v-if="v$.lead.organisation.required && v$.lead.organisation.required.$invalid">Organisation is required.</span>
            <span v-else-if="v$.lead.organisation.validOrg && v$.lead.organisation.validOrg.$invalid">Special characters (&lt;, &gt;, &quot;, `, \) are not allowed.</span>
         </span>
      </div>

      <div class="col-12">
         <label for="abn" class="form-label fw-bold">
            {{ businessNumberLabel }}
         </label>
         <input type="text" id="abn" v-model.trim="lead.abn" @keyup="handleAbnInput" @blur="handleAbnBlur" class="form-control"
            :placeholder="businessNumberPlaceholder" :disabled="abnLookup.isLoading || isAuthSynced" />

         <p v-if="abnLookup.isLoading" class="form-text text-info mb-0">Validating {{ businessNumberShortLabel }}...</p>
         <p v-if="!abnLookup.isLoading && abnLookup.message" class="form-text mb-0"
            :class="abnLookup.status === 'success' ? 'text-success' : 'text-danger'">
            {{ abnLookup.message }}
         </p>
      </div>

      <div class="col-12 my-1">
         <label class="checkbox-label" @click.stop>
            <input type="checkbox" id="isCharity" v-model="lead.isCharity" class="styled-checkbox" :disabled="isAuthSynced">
            <span class="checkbox-custom"></span>
            <strong>Is a registered charity?</strong>
         </label>
      </div>

      <div class="col-12">
         <label for="country" class="form-label fw-bold">Country</label>
         <VueMultiselect v-model="lead.selectedCountry" :options="countryOptions" :multiple="false"
            :close-on-select="true" :clear-on-select="false" :preserve-search="true" placeholder="Select a country"
            label="name" track-by="code" :preselect-first="false" :disabled="lead.isCharity || isAuthSynced"
            select-label="" deselect-label="" selected-label="">
            <template #option="{ option }">
               <div class="option-with-flag">
                  <span class="flag">{{ option.flag }}</span>
                  &nbsp;
                  <span>{{ option.name }}</span>
               </div>
            </template>

            <template #singleLabel="{ option }">
               <div class="option-with-flag">
                  <span class="flag">{{ option.flag }}</span>
                  &nbsp;
                  <span>{{ option.name }}</span>
               </div>
            </template>
         </VueMultiselect>
      </div>

      <div class="col-12 col-md-6">
         <label for="firstName" class="form-label fw-bold">First Name <span class="text-danger">*</span> </label>
         <input type="text" id="firstName" v-model.trim="lead.firstName" @blur="v$.lead.firstName.$touch"
            class="form-control" placeholder="First Name" :disabled="isAuthSynced" />

         <span class="form-text text-danger" v-if="v$.lead.firstName.$error">
            First Name is required.
         </span>
      </div>

      <div class="col-12 col-md-6">
         <label for="lastName" class="form-label fw-bold">Last Name <span class="text-danger">*</span> </label>
         <input type="text" id="lastName" v-model.trim="lead.lastName" @blur="v$.lead.lastName.$touch"
            class="form-control" placeholder="Last Name" :disabled="isAuthSynced" />

         <span class="form-text text-danger" v-if="v$.lead.lastName.$error">
            Last Name is required.
         </span>
      </div>

      <div class="col-12 col-md-6">
         <label for="email" class="form-label fw-bold">Email <span class="text-danger">*</span> </label>
         <input type="email" id="email" v-model.trim="lead.email" @blur="v$.lead.email.$touch" class="form-control"
            placeholder="Email" :disabled="isAuthSynced" />

         <span class="form-text text-danger error-msg" v-if="v$.lead.email.$error && v$.lead.email.required.$invalid">
            Email is required.
         </span>
         <span class="form-text text-danger error-msg" v-if="v$.lead.email.$error && v$.lead.email.email.$invalid">
            Must be a valid email.
         </span>
      </div>

      <div class="col-12 col-md-6">
         <label for="contactNumber" class="form-label fw-bold">Phone Number</label>
         <vue-tel-input id="contactNumber" v-model.trim="lead.contactNumber" v-bind="vueTel.props"
            mode="international" :disabled="isAuthSynced"></vue-tel-input>
      </div>

      <div v-if="isEditing" class="col-12">
         <label for="leadType" class="form-label fw-bold">Lead Type <span class="text-danger">*</span> </label>
         <VueMultiselect id="leadType" v-model="selectedLeadType" :options="selectableLeadTypes"
            :multiple="false" :close-on-select="true" :searchable="false"
            placeholder="Please choose..." label="display_name" track-by="id"
            :disabled="isAuthSynced" @close="v$.lead.leadType.$touch()"
            select-label="" deselect-label="" selected-label="">
         </VueMultiselect>

         <span class="form-text text-danger" v-if="v$.lead.leadType.$dirty && lead.leadType == ''">
            Lead Type is required.
         </span>
      </div>

      <div v-if="!isEditing" class="col-12 col-md-6">
         <label for="leadType" class="form-label fw-bold">Lead Type <span class="text-danger">*</span> </label>
         <VueMultiselect id="leadType" v-model="selectedLeadType" :options="selectableLeadTypes"
            :multiple="false" :close-on-select="true" :searchable="false"
            placeholder="Please choose..." label="display_name" track-by="id"
            :disabled="isAuthSynced" @close="v$.lead.leadType.$touch()"
            select-label="" deselect-label="" selected-label="">
         </VueMultiselect>

         <span class="form-text text-danger" v-if="v$.lead.leadType.$dirty && lead.leadType == ''">
            Lead Type is required.
         </span>
      </div>

      <div class="col-12 col-md-6">
         <label for="priority" class="form-label fw-bold">Priority <span class="text-danger">*</span> </label>
         <VueMultiselect id="priority" v-model="selectedPriority" :options="leadPriorityTypes"
            :multiple="false" :close-on-select="true" :searchable="false"
            placeholder="Please choose..." label="display_name" track-by="id"
            :disabled="isAuthSynced || isLeadStatusSpam" @close="v$.lead.priority.$touch()"
            select-label="" deselect-label="" selected-label="">
            <template #option="{ option }">
               <span class="spark-pill-badge" :class="getLeadPriorityBadgeClass(option.id)"><i :class="getLeadPriorityIcon(option.id)"></i> {{ option.display_name }}</span>
            </template>
            <template #singleLabel="{ option }">
               <span class="spark-pill-badge" :class="getLeadPriorityBadgeClass(option.id)"><i :class="getLeadPriorityIcon(option.id)"></i> {{ option.display_name }}</span>
            </template>
         </VueMultiselect>

         <span class="form-text text-danger" v-if="v$.lead.priority.$dirty && lead.priority == ''">
            Priority is required.
         </span>
      </div>

      <div v-if="isEditing" class="col-12 col-md-6">
         <label for="leadStatus" class="form-label fw-bold">Lead Status <span class="text-danger">*</span> </label>
         <select class="form-select" id="leadStatus" v-model.trim="lead.leadStatus" @blur="v$.lead.leadStatus.$touch"
            @change="onLeadStatusChange" :disabled="isAuthSynced">
            <option selected disabled value>Please choose...</option>
            <option v-for="leadStatus in leadStatusTypes" :value="leadStatus.id" :key="leadStatus.id">
               {{ leadStatus.display_name }}</option>
         </select>

         <span class="form-text text-danger" v-if="v$.lead.leadStatus.$dirty && lead.leadStatus == ''">
            Lead Status is required.
         </span>
      </div>



      <!-- LOST LEAD REASON -->
      <div v-if="isLeadStatusLost" class="col-12">
         <LostLeadForm ref="lostLeadFormRef" :isLeadStatusLost="isLeadStatusLost" :reason="lead.lostLeadReason" />
      </div>
      <!-- END --LOST LEAD REASON -->

      <div class="col-12">
         <label for="products" class="form-label fw-bold">Products <span class="text-danger">*</span></label>
         <VueMultiselect id="products" v-model="lead.products" :options="productList" :multiple="true"
            :close-on-select="true" label="display_name" track-by="id" :searchable="false"
            placeholder="Please choose at least one..." :disabled="isAuthSynced"
            select-label="" deselect-label="" selected-label="">
         </VueMultiselect>
      </div>

      <div v-if="$store.state.auth.isSuperAdmin || $store.state.auth.isAdmin || $store.state.auth.isConsultant" class="col-12">
         <div class="d-flex align-items-center justify-content-between mb-1">
            <label for="assigned_user" class="form-label fw-bold mb-0">Assigned BDM <span class="text-danger">*</span></label>
            <div>
               <span v-if="bdmSaveState === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                  <i class="fa-solid fa-spinner fa-spin text-warning me-1"></i> <small class="text-muted">Waiting to save...</small>
               </span>
               <span v-else-if="bdmSaveState === 'saving'" class="status-text saving" title="Saving changes...">
                  <i class="fa-solid fa-cloud-arrow-up text-info me-1"></i> <small class="text-info">Saving...</small>
               </span>
               <span v-else-if="bdmSaveState === 'saved'" class="status-text saved" title="Saved!">
                  <i class="fa-solid fa-circle-check text-success me-1"></i> <small class="text-success">Saved!</small>
               </span>
            </div>
         </div>
         <VueMultiselect id="assigned_user" v-model="selectedBDM" :options="bdmOptions"
            :multiple="false" :close-on-select="true" :searchable="true"
            placeholder="Select a BDM" label="displayName" track-by="auth_user_id"
            open-direction="bottom" select-label="" deselect-label="" selected-label=""
            @close="v$.lead.assigned_user_id.$touch()">
         </VueMultiselect>

         <span class="form-text text-danger" v-if="v$.lead.assigned_user_id.$error">
            Assigned BDM is required.
         </span>
      </div>

      <div class="col-12">
         <label for="enquiryDetails" class="form-label fw-bold">Enquiry Details <span class="text-danger">*</span> </label>
         <textarea class="form-control" id="enquiryDetails" v-model.trim="lead.enquiryDetails"
            @blur="v$.lead.enquiryDetails.$touch" placeholder="Enquiry Details" style="height: 120px"></textarea>

         <span class="form-text text-danger" v-if="v$.lead.enquiryDetails.$error">
            Enquiry Details is required.
         </span>
      </div>

      <div class="col-12 pt-3 pb-2 justify-content-center d-flex gap-2">
         <button v-if="!isEditing" type="button" class="btn btn-outline-warning fw-bold"
            style="border-radius: 8px; padding: 8px 24px; width: auto;"
            @click="resetForm(); setDefaultCountryCode();">Clear fields</button>
         <button v-if="!isEditing" type="button" class="btn btn-info text-light fw-bold"
            style="border-radius: 8px; padding: 8px 36px; width: auto;"
            @click="createLead()" :disabled="disableFormButton()"> Create </button>

         <button v-if="isEditing" type="button" class="btn btn-warning text-light fw-bold"
            style="border-radius: 8px; padding: 8px 24px; width: auto;"
            @click="cancelEdit()"> Cancel </button>
         <button v-if="isEditing" type="button" class="btn btn-info text-light fw-bold"
            style="border-radius: 8px; padding: 8px 36px; width: auto;"
            @click="updateLead(lead.id)" :disabled="disableFormButton()"> Update </button>
      </div>
   </form>

   <ConfirmModal
      v-model="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      @confirm="onConfirmAction"
   />
</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email } from '@vuelidate/validators'
import { isEmpty } from '@/plugins/helper.js'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import LostLeadForm from '@/components/Leads/LostLeadForm.vue'
import { countries } from 'country-data';
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
import { Modal, Offcanvas } from 'bootstrap';
import {
   isAustralianCountry,
   getBusinessNumberLabel,
   getBusinessNumberShortLabel,
   getBusinessNumberPlaceholder,
   validateBusinessNumber,
} from '@/plugins/businessValidator.js';
const orgNameValidator = (value) => !value || !/[<>"\\`]/.test(value)
const _ = require('lodash');

export default {

   components: {
      VueTelInput,
      VueMultiselect,
      LostLeadForm,
      LoadingPage,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   emits: ['onSubmit'],

   props: {
      item: {
         type: Object,
         // required: true,
      },

   },

   data() {
      return {
         abnDetails: null,
         abnRecordFound: false,
         abnLookup: { isLoading: false, status: 'idle', message: '' },
         abnDebounceTimer: null,
         bdmSaveState: 'idle',
         bdmDebounceTimer: null,
         currentDate: null,
         lead: {
            dateCreated: null,
            organisation: '',
            original_organisation_name: '',
            firstName: '',
            lastName: '',
            email: '',
            contactNumber: '',
            abn: '',
            isCharity: false,
            leadType: '',
            leadStatus: '',
            priority: '',
            products: [],
            enquiryDetails: '',
            lostLeadReason: '',
            marketing_channel: '',
            selectedCountry: null,
            classification: 'Lead',
            isTestData: false,
            has_auth_organisation: 0,
            is_auth_synced: false,
            bdm_id: null,
            assigned_user_id: 0,
         },
         bdms: [],
         internalUsers: [],
         countryOptions: countries.all.map(country => ({
            code: country.alpha2,
            name: country.name,
            flag: country.emoji,
         })),
         leadTypes: [],
         marketingChannels: [],
         leadPriorityTypes: [],
         productList: [],
         leadStatusTypes: [],
         vueTel: {
            props: {
               preferredCountries: ['AU', 'US'],
               defaultCountry: "AU",
               validCharactersOnly: true,
               dropdownOptions: {
                  showDialCodeInList: true,
                  showFlags: true,
                  showSearchBox: true,
               },
               inputOptions: {
                  autocomplete: "on",
                  showDialCode: true,
                  placeholder: "Contact Number",
                  styleClasses: "form-control",
               },
            }
         },
         isEditing: false,
         isLeadStatusLost: false,
         isLoading: false,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingDeleteId: null,
      }
   },

   validations() {
      return {
         lead: {
            organisation: { required, validOrg: orgNameValidator },
            firstName: { required },
            lastName: { required },
            email: { required, email },
            abn: { 
               validBusinessNumber: (value) => {
                  const countryCode = this.lead.selectedCountry?.code || this.lead.country || 'AU';
                  return validateBusinessNumber(value, countryCode);
               }
            },
            leadType: { required },
            leadStatus: { requiredIf: requiredIf(this.isEditing) },
            priority: { required },
            enquiryDetails: { requiredIf: requiredIf(!this.isEditing) },
            assigned_user_id: {
               required: requiredIf(() => {
                  return !!(this.$store.state.auth.isSuperAdmin || this.$store.state.auth.isAdmin || this.$store.state.auth.isConsultant);
               }),
               mustBeSelected: (value) => {
                  if (this.$store.state.auth.isSuperAdmin || this.$store.state.auth.isAdmin || this.$store.state.auth.isConsultant) {
                     return value !== 0 && value !== null && value !== undefined;
                  }
                  return true;
               }
            },
         },
      }
   },

   computed: {
      leadCountryCode() {
         return this.lead.selectedCountry?.code || this.lead.country || 'AU';
      },
      isAustralianLead() {
         return isAustralianCountry(this.leadCountryCode);
      },
      businessNumberLabel() {
         return getBusinessNumberLabel(this.leadCountryCode);
      },
      businessNumberShortLabel() {
         return getBusinessNumberShortLabel(this.leadCountryCode);
      },
      businessNumberPlaceholder() {
         return getBusinessNumberPlaceholder(this.leadCountryCode);
      },
      isLeadStatusSpam() {
         const spamStatus = this.leadStatusTypes.find(s => s.name === 'Spam');
         return !!(spamStatus && this.lead.leadStatus == spamStatus.id);
      },
      isAuthSynced() {
         if (!this.isEditing) return false;
         const authSyncType = this.leadTypes.find(t => t.name === 'AuthSync');
         const isAuthType = !!(authSyncType && this.lead.leadType === authSyncType.id);
         return isAuthType || this.lead.has_auth_organisation === 1 || this.lead.has_auth_organisation === 2 || this.lead.is_auth_synced === true;
      },
      selectableLeadTypes() {
         return this.leadTypes.filter(type => {
            if (type.name === 'AuthSync') {
               return this.isEditing && this.lead.leadType === type.id;
            }
            return true;
         });
      },
      isAustraliaResident() {
         return this.lead.selectedCountry && (this.lead.selectedCountry.code === 'AU' || this.lead.selectedCountry.name === 'Australia');
      },
      organisationNameOptions() {
         if (!this.abnRecordFound || !this.abnDetails) return [];
         const names = [];
         if (this.lead.organisation) {
            names.push(this.lead.organisation);
         }
         names.push(this.abnDetails.entity_name);
         if (this.abnDetails.business_names && this.abnDetails.business_names.length > 0) {
            const businessNames = this.abnDetails.business_names.map(bn => bn.name).filter(Boolean);
            names.push(...businessNames);
         }
         return [...new Set(names)];
      },

      lostLeadStatus() {
         return this.lead.leadStatus
      },
      invalidFields() {
         if (!this.v$ || !this.v$.lead) return [];
         return Object.keys(this.v$.lead).filter(key => this.v$.lead[key] && this.v$.lead[key].$invalid);
      },

      bdmOptions() {
         return this.bdms.map(bdm => {
            const displayName = bdm.name || (bdm.first_name + ' ' + bdm.last_name).trim() || bdm.email;
            const extraEmail = bdm.email && (bdm.name || bdm.first_name || bdm.last_name) ? ` (${bdm.email})` : '';
            return {
               ...bdm,
               displayName: `${displayName}${extraEmail}`
            };
         });
      },

      selectedBDM: {
         get() {
            return this.bdmOptions.find(bdm => bdm.auth_user_id === this.lead.assigned_user_id) || null;
         },
         set(val) {
            const oldVal = this.lead.assigned_user_id;
            this.lead.assigned_user_id = val ? val.auth_user_id : 0;
            this.v$.lead.assigned_user_id.$touch();
            if (this.isEditing && oldVal !== this.lead.assigned_user_id) {
               const currentUserId = this.$store.state.auth.currentUser?.id || this.$store.state.auth.jwtPayload?.user_id;
               const isConsultant = this.$store.state.auth.isConsultant;
               const assignedToOther = this.lead.assigned_user_id && this.lead.assigned_user_id !== currentUserId;
               if (isConsultant && assignedToOther) {
                  this.saveBdmAndClose();
               } else {
                  this.triggerBdmAutoSave();
               }
            }
         }
      },

      selectedLeadType: {
         get() {
            return this.leadTypes.find(type => type.id === this.lead.leadType) || null;
         },
         set(val) {
            this.lead.leadType = val ? val.id : '';
            this.v$.lead.leadType.$touch();
         }
      },

      selectedPriority: {
         get() {
            return this.leadPriorityTypes.find(p => p.id === this.lead.priority) || null;
         },
         set(val) {
            this.lead.priority = val ? val.id : '';
            this.v$.lead.priority.$touch();
         }
      },

   },

    watch: {
       'lead.isCharity'(newVal) {
          if (newVal) {
             this.lead.selectedCountry = this.getCountryByCode(this.countryOptions, "AU");
             if (this.lead.abn) {
                this.lookupABN();
             }
          } else {
             if (this.lead.abn) {
                this.lookupABN();
             } else {
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
             }
          }
       },

       isAustraliaResident(newVal, oldVal) {
          if (newVal) {
             if (this.lead.abn) {
                this.lookupABN();
             }
          } else if (oldVal === true) {
             if (!this.lead.isCharity) {
                this.lead.abn = '';
                this.abnDetails = null;
                this.abnRecordFound = false;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
             }
          }
       },

       lostLeadStatus() {
         if (this.lead.leadStatus == this.lostLeadStatusId()) {
            this.isLeadStatusLost = true
         } else {
            this.isLeadStatusLost = false
         }
      },

      internalUsers: {
         handler(newVal) {
            if (newVal && newVal.length > 0 && !this.lead.assigned_user_id && !this.isEditing) {
               const currentUser = this.$store.state.auth.currentUser;
               if (currentUser) {
                  const me = newVal.find(u => u.id == currentUser.id);
                  if (me) {
                     this.lead.assigned_user_id = me.id;
                  }
               }
            }
         },
         immediate: true
      }
   },

   created() {
      this.init()
   },

   beforeUnmount() {
      if (this.bdmDebounceTimer) {
         clearTimeout(this.bdmDebounceTimer);
      }
   },

    methods: {
       closeModalOrPanel() {
          const modalEl = document.getElementById('leadForm');
          if (modalEl && modalEl.classList.contains('show')) {
             const modalInstance = Modal.getInstance(modalEl) || Modal.getOrCreateInstance(modalEl);
             if (modalInstance) {
                modalInstance.hide();
             }
          }
          const sidebarEl = document.getElementById('leadSidebar');
          if (sidebarEl && sidebarEl.classList.contains('show')) {
             const offcanvasInstance = Offcanvas.getInstance(sidebarEl) || Offcanvas.getOrCreateInstance(sidebarEl);
             if (offcanvasInstance) {
                offcanvasInstance.hide();
             }
          }
       },

       async saveBdmAndClose() {
          if (this.bdmDebounceTimer) {
             clearTimeout(this.bdmDebounceTimer);
             this.bdmDebounceTimer = null;
          }
          this.bdmSaveState = 'saving';
          const payload = {
             assigned_user_id: this.lead.assigned_user_id ? parseInt(this.lead.assigned_user_id) : 0
          };

          try {
             const response = await this.$axios.put(`/leads/${this.lead.id}/assigned-user`, payload);
             if (response.data.success) {
                this.$toast.success("Lead assigned successfully!");
                if (this._beforeEditingCache) {
                   this._beforeEditingCache.assigned_user_id = this.lead.assigned_user_id;
                }
                this.closeModalOrPanel();
                this.$emit('onSubmit', { assigned_user_id: this.lead.assigned_user_id });
             } else {
                this.$toast.error(response.data.message || "Failed to save BDM.");
             }
          } catch (err) {
             console.error("Error saving Assigned BDM:", err);
             this.$toast.error(err.response?.data?.message || "Failed to save Assigned BDM.");
          } finally {
             this.bdmSaveState = 'idle';
          }
       },

       triggerBdmAutoSave() {
          this.bdmSaveState = 'waiting';

          if (this.bdmDebounceTimer) {
             clearTimeout(this.bdmDebounceTimer);
          }

          this.bdmDebounceTimer = setTimeout(async () => {
             this.bdmSaveState = 'saving';
             try {
                await this.saveBdmOnly();
                this.bdmSaveState = 'saved';
                setTimeout(() => {
                   if (this.bdmSaveState === 'saved') {
                      this.bdmSaveState = 'idle';
                   }
                }, 2000);
             } catch (err) {
                this.bdmSaveState = 'idle';
             }
             this.bdmDebounceTimer = null;
          }, 3000); // 3 seconds, same as organisations
       },

       async saveBdmOnly() {
          const payload = {
             assigned_user_id: this.lead.assigned_user_id ? parseInt(this.lead.assigned_user_id) : 0
          };

          try {
             const response = await this.$axios.put(`/leads/${this.lead.id}/assigned-user`, payload);
             if (response.data.success) {
                this.$toast.success("Assigned BDM saved successfully!");
                if (this._beforeEditingCache) {
                   this._beforeEditingCache.assigned_user_id = this.lead.assigned_user_id;
                }
                const currentUserId = this.$store.state.auth.currentUser?.id || this.$store.state.auth.jwtPayload?.user_id;
                const isConsultant = this.$store.state.auth.isConsultant;
                const assignedToOther = this.lead.assigned_user_id && this.lead.assigned_user_id !== currentUserId;
                if (isConsultant && assignedToOther) {
                   this.closeModalOrPanel();
                }
                this.$emit('onSubmit', { assigned_user_id: this.lead.assigned_user_id });
             } else {
                this.$toast.error(response.data.message || "Failed to save BDM.");
                throw new Error(response.data.message);
             }
          } catch (err) {
             console.error("Error auto-saving Assigned BDM:", err);
             this.$toast.error(err.response?.data?.message || "Failed to save Assigned BDM.");
             throw err;
          }
       },

      handleAbnInput() {
         clearTimeout(this.abnDebounceTimer);
         if (!this.lead.abn || this.lead.abn.trim() === '') {
            this.abnRecordFound = false;
            this.abnDetails = null;
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            return;
         }
         this.abnDebounceTimer = setTimeout(() => { this.lookupABN(true); }, 1000);
      },
      handleAbnBlur() {
         this.v$.lead.abn.$touch();
         if (this.abnDebounceTimer) {
            clearTimeout(this.abnDebounceTimer);
            this.abnDebounceTimer = null;
            this.lookupABN(true);
         }
      },
      async lookupABN(isManual = false) {
         if (!this.lead.abn || this.lead.abn.trim() === '') {
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }

         if (!this.isAustralianLead) {
            const isValid = validateBusinessNumber(this.lead.abn, this.leadCountryCode);
            if (isValid) {
               this.abnLookup = { isLoading: false, status: 'success', message: `${this.businessNumberShortLabel} format valid.` };
            } else {
               this.abnLookup = { isLoading: false, status: 'error', message: `Invalid ${this.businessNumberShortLabel} format.` };
            }
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }

         const abn = this.lead.abn.replace(/\s/g, '');
         if (!abn || (abn.length !== 11 && abn.length !== 9)) {
            this.abnLookup = { isLoading: false, status: 'error', message: abn.length > 0 ? 'ABN must be 11 digits (or 9 digits for ACN).' : '' };
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }
         this.abnLookup.isLoading = true;
         this.abnLookup.message = '';
         this.abnLookup.status = 'idle';
         try {
            const resp = await this.$axios.post('/abn/look-up', {
               abn: abn,
            });
            const details = resp.data.abn_details;
            this.abnDetails = details;
            this.abnRecordFound = true;

            if (this.lead.isCharity && !details.is_charity) {
               this.abnLookup.status = 'error';
               this.abnLookup.message = 'ABN is found but not registered as a charity.';
            } else {
               this.abnLookup.status = 'success';
               this.abnLookup.message = 'ABN Validated successfully.';
               if (isManual || !this.lead.organisation) {
                  this.lead.organisation = details.entity_name;
               }
            }
         } catch (error) {
            this.abnRecordFound = false;
            this.abnDetails = null;
            this.abnLookup.status = 'error';
            this.abnLookup.message = error.response?.data?.message || 'Invalid ABN';
         } finally {
            this.abnLookup.isLoading = false;
         }
      },

      init() {
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
         this.getLeadTypeList()
         this.getLeadPriorityList()
         this.getProductList()
         this.getLeadStatusList()
         this.getMarketingChannelList()
      },

      async getInternalUsers() {
         try {
            const [userResp, bdmResp] = await Promise.all([
               this.$axios.get("/auth/users/super-admin"),
               this.$axios.get("/vertex-bdm")
            ]);

            if (userResp.data.success) {
               this.internalUsers = userResp.data.super_admin_users || [];
               const bdms = bdmResp.data.success ? (bdmResp.data.bdms || []) : [];
               this.bdms = bdms;

                 // Auto-select logic
                 if (!this.lead.assigned_user_id && !this.isEditing) {
                    const currentUser = this.$store.state.auth.currentUser;
                    // 1. Check if logged-in user exists in the BDM list
                    const asBdm = this.bdms.find(b => b.auth_user_id == currentUser.id);
                    if (asBdm) {
                       this.lead.assigned_user_id = asBdm.auth_user_id;
                    } else if (this.internalUsers.length > 0) {
                       // 2. Fallback to matching super admin users list
                       const me = this.internalUsers.find(u => u.id == currentUser.id);
                       if (me) {
                          this.lead.assigned_user_id = me.id;
                       }
                    }
                 }
            }
         } catch (error) {
            console.error("Error fetching internal users or BDMs:", error);
         }
      },

      isCreate() {
         this.isEditing = false
         this.lead.selectedCountry = this.getCountryByCode(this.countryOptions, "AU")
         this.setDefaultCountryCode()
         this.getInternalUsers()
      },

      isEdit(id) {
         this.isEditing = true
         this.getLead(id)
         this.getInternalUsers()

         this._beforeEditingCache = _.cloneDeep(this.lead);
      },

      isDelete(id) {
         this.deleteLead(id)
      },

      resetForm() {
         this.lead.organisation = ""
         this.lead.firstName = ""
         this.lead.lastName = ""
         this.lead.email = ""
         this.lead.abn = ""
         this.lead.isCharity = false
         this.lead.leadType = ""
         this.lead.leadStatus = ""
         this.lead.priority = ""
         this.lead.products = []
         this.lead.enquiryDetails = ""
         this.lead.lostLeadReason = ""
         this.isEditing = false
         this.isLeadStatusLost = false
         this.lead.marketing_channel = ""
         this.lead.has_auth_organisation = 0
         this.lead.selectedCountry = this.getCountryByCode(this.countryOptions, "AU")
         this.lead.classification = 'Lead'
         this.lead.isTestData = false
         this.lead.bdm_id = null
         this.lead.assigned_user_id = 0
         this.abnDetails = null
         this.abnRecordFound = false
         this.abnLookup = { isLoading: false, status: 'idle', message: '' }
         this.v$.$reset()
      },

      setDefaultCountryCode() {
         this.$refs.leadFormRef.querySelectorAll(".vti__flag")[0].className = "vti__flag au"
         this.lead.contactNumber = "+61"
      },

      disableFormButton() {

         if (this.isAuthSynced) {
            return true
         }

         if (this.isEditing) {

            if ((this._beforeEditingCache != '') &&
               _.isEqual(this._beforeEditingCache, this.lead)) {

               return true
            } else {

               const difference = _.differenceWith(_.toPairs(this._beforeEditingCache), _.toPairs(this.lead), _.isEqual)

               if (difference.length == 1) {
                  if (difference[0][0] == 'contactNumber') {
                     if (this.lead.contactNumber.replaceAll(" ", "") == this._beforeEditingCache.contactNumber.replaceAll(" ", "")) {
                        return true
                     }
                  }

               }

            }

            if (this.v$.$invalid || this.v$.$error ||
               this.lead.leadType == '' || this.lead.priority == '' ||
               this.lead.leadStatus == '') {
               return true
            }

         } else {
            if (this.v$.$invalid || this.v$.$error ||
               this.lead.leadType == '' || this.lead.priority == '' || this.lead.products == [] || this.lead.products.length == 0) {
               return true
            }

         }

         if (this.lead.abn && this.lead.abn.trim() !== '' && this.abnLookup.status !== 'success') {
            return true
         }

         return false
      },

      cancelEdit() {

         Object.assign(this.lead, this._beforeEditingCache);
         if (this.lead.abn) {
            this.lookupABN();
         } else {
            this.abnDetails = null;
            this.abnRecordFound = false;
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
         }
      },

      async getMarketingChannelList() {
         await this.$axios.get("/categories?name=MarketingChannel")
            .then((response) => {
               this.marketingChannels = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLead(id) {

         await this.$axios.get("/leads/" + id)
            .then((response) => {
               this.lead.id = response.data.lead.id
               this.lead.dateCreated = moment(new Date(response.data.lead.date_created)).format("DD-MMM-YYYY")
               this.lead.organisation = response.data.lead.organisation_name
               this.lead.original_organisation_name = response.data.lead.original_organisation_name || ''
               this.lead.firstName = response.data.lead.first_name
               this.lead.lastName = response.data.lead.last_name
               this.lead.email = response.data.lead.email
               this.lead.abn = response.data.lead.abn
               this.lead.isCharity = response.data.lead.is_charity
               this.lead.contactNumber = response.data.lead.phone_number
               this.lead.leadType = response.data.lead.lead_type_id
               this.lead.leadStatus = response.data.lead.lead_status_id
               this.lead.priority = response.data.lead.lead_priority_id
               this.lead.marketing_channel = (!isEmpty(response.data.lead.marketing_channel)) ? response.data.lead.marketing_channel.id : '';
               this.lead.classification = response.data.lead.classification || 'Lead'
               this.lead.isTestData = response.data.lead.is_test_data || false
               this.lead.bdm_id = (response.data.lead.bdm_id && response.data.lead.bdm_id !== 0) ? response.data.lead.bdm_id : null
               this.lead.assigned_user_id = response.data.lead.assigned_user_id || 0
               this.lead.enquiryDetails = response.data.lead.enquiry_details || ''
               this.lead.has_auth_organisation = response.data.lead.organisation ? (response.data.lead.organisation.has_auth_organisation || 0) : 0
               this.lead.is_auth_synced = response.data.lead.organisation ? (response.data.lead.organisation.is_auth_synced || false) : false
               let cc = response.data.lead.country_code
               let c = this.getCountryByCode(this.countryOptions, cc)
               this.lead.selectedCountry = (c != null || c != undefined || c != isEmpty(c)) ? c : null;

               // display AU when there is no contact number saved
               if (this.lead.contactNumber.trim() == "") {
                  this.setDefaultCountryCode()
               }

               // identify if lead is a lost lead, then get reason
               if (this.lostLeadStatusId() == this.lead.leadStatus) {
                  this.lead.lostLeadReason = response.data.lead.lead_notes[0].note_details
               }

               this.lead.products = []
               response.data.lead.products.forEach((p) => {
                  let displayName = this.productList.filter(e => { return e.id == p.product_id }).map(e => (e.display_name)).toString()
                  this.lead.products.push({
                     id: p.product_id,
                     display_name: displayName
                  })
               })

               // Keep track of the original data
               this._beforeEditingCache = _.cloneDeep(this.lead);

                if (this.lead.abn) {
                   this.lookupABN();
                }

            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })

         //get the format of contact number
         this._beforeEditingCache.contactNumber = this.lead.contactNumber


      },


      onLeadStatusChange() {
         this.v$.lead.leadStatus.$touch();

         // Auto-set priority to NA for specific statuses (Spam, Lost, test_only, etc.)
         const specificStatuses = ['Spam', 'Lost', 'test_only', 'PartnerLead', 'Recruitment', 'pr_opportunity'];
         const isSpecific = specificStatuses.some(status => {
            const matched = this.leadStatusTypes.find(s => s.name === status);
            return matched && this.lead.leadStatus == matched.id;
         });
         if (isSpecific) {
            const naPriority = this.leadPriorityTypes.find(p => p.name === 'not_applicable');
            if (naPriority) this.lead.priority = naPriority.id;
         }
      },

      //retrieve id of lost lead status
      lostLeadStatusId() {
         return this.leadStatusTypes.filter(e => { return e.name == 'Lost' }).map(e => (e.id)).toString()
      },

      getCountryByCode(countries, countryCode) {
         return countries.find(country => country.code.toLowerCase() === countryCode.toLowerCase());
      },

      handleOrgInput() {
         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '');
         }
      },

      async createLead() {
         this.isLoading = true
         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '').trim();
         }
         if (this.lead.contactNumber.trim().length < 5) {
            this.lead.contactNumber = ''
         }

         let lead = {
            organisation_name: this.lead.organisation,
            first_name: this.lead.firstName,
            last_name: this.lead.lastName,
            email: this.lead.email,
            phone_number: this.lead.contactNumber,
            abn: this.lead.abn,
            is_charity: this.lead.isCharity,
            lead_type_id: this.lead.leadType,
            lead_priority_id: this.lead.priority,
            marketing_channel_id: this.lead.marketing_channel,
            lead_notes: [{
               note_details: this.lead.enquiryDetails
            }],
            country_code: (this.lead.selectedCountry != null) ? this.lead.selectedCountry.code : null,
            classification: this.lead.classification,
            is_test_data: this.lead.isTestData,
            bdm_id: this.lead.bdm_id ? parseInt(this.lead.bdm_id) : 0,
            assigned_user_id: this.lead.assigned_user_id ? parseInt(this.lead.assigned_user_id) : 0
         }

         lead.products = []
         this.lead.products.forEach((p) => {
            lead.products.push({
               product_id: p.id.toString()
            })
         })


         await this.$axios.post("/leads", lead)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)

                  // this.$refs.closeModal.click();
                  this.resetForm();
                  this.isLoading = false
                  this.$emit('onSubmit')
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.isLoading = false
               this.$toast.error(error.response.data.message)
            })
         this.isLoading = false
         this.resetForm();
      },

      async updateLead(id) {
         this.isLoading = true
         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '').trim();
         }
         if (this.lead.contactNumber.trim().length < 5) {
            this.lead.contactNumber = ''
         }

         let lead = {
            organisation_name: this.lead.organisation,
            first_name: this.lead.firstName,
            last_name: this.lead.lastName,
            email: this.lead.email,
            phone_number: this.lead.contactNumber,
            abn: this.lead.abn,
            is_charity: this.lead.isCharity,
            lead_type_id: this.lead.leadType,
            lead_status_id: this.lead.leadStatus,
            lead_priority_id: this.lead.priority,
            marketing_channel_id: this.lead.marketing_channel,
            country_code: (this.lead.selectedCountry != null) ? this.lead.selectedCountry.code : null,
            classification: this.lead.classification,
            is_test_data: this.lead.isTestData,
            bdm_id: this.lead.bdm_id ? parseInt(this.lead.bdm_id) : 0,
            assigned_user_id: this.lead.assigned_user_id ? parseInt(this.lead.assigned_user_id) : 0,
            enquiry_details: this.lead.enquiryDetails
         }

         //products
         lead.products = []
         this.lead.products.forEach((p) => {
            lead.products.push({
               product_id: p.id.toString()
            })
         })

         // add lead notes when lead will be changed to lost lead
         // will not add if lead is already a lost lead
         if (this.isLeadStatusLost && !this.isLostLead) {

            // save reason for the dropdown or the specified reason
            let noteDetails = this.$refs.lostLeadFormRef.lostLeadReasonValue()
            // if (this.$store.state.lead.isOtherLostLeadReason) {
            //    noteDetails = this.otherLostLeadReason
            // } else {
            //    noteDetails = this.$refs.lostLeadFormRef.lostLeadReasonValue(this.lead.lostLeadReason)
            // }

            lead.lead_notes = []
            lead.lead_notes.push({
               note_details: noteDetails
            })
         }

         await this.$axios.put("/leads/" + id, lead)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)
                  this.resetForm();
                  this.isLoading = false
                  const currentUserId = this.$store.state.auth.currentUser?.id || this.$store.state.auth.jwtPayload?.user_id;
                  const isConsultant = this.$store.state.auth.isConsultant;
                  const assignedToOther = lead.assigned_user_id && lead.assigned_user_id !== currentUserId;
                  if (isConsultant && assignedToOther) {
                     this.closeModalOrPanel();
                  }
                  this.$emit('onSubmit', { assigned_user_id: lead.assigned_user_id })
               } else {
                  this.isLoading = false
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.isLoading = false
               this.$toast.error(error.response?.data?.message || error.response?.data || "Unable to update lead.")
            })
      },

      async deleteLead(id) {
         this.pendingDeleteId = id;
         this.confirmModalTitle = 'Delete Lead';
         this.confirmModalMessage = 'Do you want to delete this lead?';
         this.showConfirmModal = true;
      },

      async onConfirmAction() {
         const id = this.pendingDeleteId;
         this.pendingDeleteId = null;
         if (!id) return;
         await this.$axios.delete("/leads/" + id)
            .then((response) => {   
               if (response.data.success) {
                  this.$toast.success(response.data.message)

                  this.$emit('onSubmit')
               } else {
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLeadTypeList() {
         await this.$axios.get("/categories?name=LeadType")
            .then((response) => {
               this.leadTypes = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLeadPriorityList() {
         await this.$axios.get("/categories?name=LeadPriority")
            .then((response) => {
               this.leadPriorityTypes = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLeadStatusList() {
         await this.$axios.get("/categories?name=LeadStatus")
            .then((response) => {
               this.leadStatusTypes = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getProductList() {
         await this.$axios.get("/categories?name=Products")
            .then((response) => {
               this.productList = []
               response.data.category.subcategories.forEach((p) => {
                  // let displayName = this.productList.filter(e => {return e.id == p.product_id}).map(e => (e.display_name)).toString()
                  this.productList.push({
                     id: p.id,
                     display_name: p.display_name
                  })
               })

            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      priorityClass(value) {
         let priority = this.leadPriorityTypes.filter((p) => { return p.id == value }).map((e) => e.name)

         return priority.toString().toLowerCase()
      },

      getLeadPriorityBadgeClass(priorityId) {
         const priority = this.leadPriorityTypes.find(p => p.id === priorityId);
         if (!priority) return 'spark-pill-badge--priority-na';
         const name = (priority.name || '').toLowerCase();
         if (name.includes('high')) return 'spark-pill-badge--priority-high';
         if (name.includes('medium')) return 'spark-pill-badge--priority-medium';
         if (name.includes('low')) return 'spark-pill-badge--priority-low';
         return 'spark-pill-badge--priority-na';
      },

      getLeadPriorityIcon(priorityId) {
         const priority = this.leadPriorityTypes.find(p => p.id === priorityId);
         if (!priority) return 'fa-solid fa-minus';
         const name = (priority.name || '').toLowerCase();
         if (name.includes('high')) return 'fa-solid fa-fire';
         if (name.includes('medium')) return 'fa-solid fa-equals';
         if (name.includes('low')) return 'fa-solid fa-arrow-down';
         return 'fa-solid fa-minus';
      },
   },
};

</script>

<style scoped>
.high, ::v-deep(.high) {
   color: #EB1C24;
}

.low, ::v-deep(.low) {
   color: #00A54F;
}

.medium, ::v-deep(.medium) {
   color: #FFB300;
}

::v-deep(.multiselect__option--highlight) .high,
::v-deep(.multiselect__option--highlight) .low,
::v-deep(.multiselect__option--highlight) .medium {
   color: #ffffff !important;
}

.modal-form-custom .form-label,
form label:not(.checkbox-label) {
   font-weight: 600;
   color: #364A63;
   font-size: 13px;
   margin-bottom: 4px;
}

.modal-form-custom .form-control,
.modal-form-custom .form-select,
input:not([type="checkbox"]),
select {
   height: 40px;
   border-radius: 8px;
   border: 1px solid #dee2e6;
   color: #1e293b;
   font-size: 14px;
   background-color: #ffffff;
   transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.modal-form-custom .form-control:focus,
.modal-form-custom .form-select:focus,
input:not([type="checkbox"]):focus,
select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.modal-form-custom textarea.form-control,
textarea.form-control {
   height: auto !important;
   border-radius: 8px;
   border: 1px solid #dee2e6;
   color: #1e293b;
   font-size: 14px;
}

.modal-form-custom textarea.form-control:focus,
textarea.form-control:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

::v-deep(.multiselect__tags) {
   border-radius: 8px !important;
   border: 1px solid #dee2e6 !important;
   min-height: 40px !important;
   padding-top: 8px !important;
}

::v-deep(.multiselect--active .multiselect__tags) {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15) !important;
}

::v-deep(.vue-tel-input) {
   border-radius: 8px !important;
   border: 1px solid #dee2e6 !important;
   height: 40px !important;
}

::v-deep(.vue-tel-input:focus-within) {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15) !important;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333 !important;
}

.checkbox-label:has(input:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

.styled-checkbox {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 12px;
  display: inline-block;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;
}

.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

.status-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  animation: fadeIn 0.3s ease;
}

.status-text.waiting {
  font-size: 0.85rem;
}

.status-text.saving {
  font-size: 0.85rem;
}

.status-text.saved {
  font-size: 0.85rem;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-3px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>